/**
 * Campanita del sider (OpenSpec 010). El badge se actualiza en vivo por WebSocket
 * (room user-{id}); el click **navega** a /notifications (sin popover, según el doc).
 * Socket propio al conversation-service → el badge vive en toda la app.
 *
 * Y porque vive en toda la app, es también de donde salen las notificaciones del navegador (#277):
 * el mismo socket ya trae `notification:new` con todo lo que el toast necesita mostrar. Escucharlo
 * en la pantalla de Notificaciones no alcanzaría — el caso del ticket es justamente el Operador que
 * NO está ahí.
 */
import React, { useEffect, useRef, useState } from "react";
import { Badge, Avatar, Button } from "antd";
import { useNavigation } from "@refinedev/core";
import { io, Socket } from "socket.io-client";
import { AlertIcon } from "@/components/icon";
import {
  AppNotification,
  CONVERSATIONS_API_URL,
  conversationsApi,
  getAccessToken,
  isConversationsConfigured,
} from "@/services/conversations.api";
import { showMention, startFocusHeartbeat } from "@/services/browser-notifications";
import { recordHref } from "@/services/notification-copy";

interface DropMenuProps {
  collapsed: boolean;
}

export const DropMenuAlert: React.FC<DropMenuProps> = ({ collapsed: _collapsed }) => {
  const { push } = useNavigation();
  const [unread, setUnread] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  // El handler del socket se registra una vez; sin el ref se quedaría con el `push` del primer
  // render y navegaría con una referencia vieja.
  const pushRef = useRef(push);
  useEffect(() => {
    pushRef.current = push;
  }, [push]);

  useEffect(() => {
    // Sin URL del conv-service no hay a dónde conectarse. Este componente vive en el sider, o sea
    // que corre en TODA pantalla y para todo usuario: sin este guard, cada carga abriría un socket
    // y un GET contra el dominio del propio admin (que responde el HTML del index).
    if (!isConversationsConfigured()) return;

    const stopHeartbeat = startFocusHeartbeat();

    conversationsApi.unreadNotifications().then(setUnread).catch(() => {});
    const socket = io(`${CONVERSATIONS_API_URL}/conversations`, {
      transports: ["websocket"],
      auth: { token: getAccessToken() },
    });
    socketRef.current = socket;
    socket.on("notification:count", (p: { unread: number }) => setUnread(p.unread));
    // La mención afuera del tab. Los guards (permiso, foco, duplicados, antigüedad) están en el
    // servicio: acá sólo se le dice a dónde llevar al Operador cuando hace click.
    socket.on("notification:new", (n: AppNotification) =>
      showMention(n, (opened) => {
        const href = recordHref(opened);
        if (href) pushRef.current(href);
      }),
    );
    // Reconexión: refrescar el count (pudo cambiar mientras estuvimos desconectados).
    socket.on("connect", () => {
      conversationsApi.unreadNotifications().then(setUnread).catch(() => {});
    });
    return () => {
      stopHeartbeat();
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  return (
    <Button type="link" style={{ padding: 0 }} onClick={() => push("/notifications")}>
      <Badge count={unread} size="small">
        <Avatar
          size="large"
          shape="square"
          icon={<AlertIcon />}
          style={{ backgroundColor: "#FFF", color: "rgba(0,0,0, 0.88)", border: "1px solid #F0F0F0" }}
        />
      </Badge>
    </Button>
  );
};
